import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";
import {
  AlertTriangle, CheckCircle2, XCircle, RefreshCw,
  Building2, ShoppingBag, Users, Package, Loader2,
  CalendarClock, Clock, WrenchIcon, Shield
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL_MS = 60_000;

function severityColor(level, darkMode) {
  if (level === "critical") return darkMode ? "text-red-400" : "text-red-600";
  if (level === "warning")  return darkMode ? "text-yellow-400" : "text-yellow-600";
  return darkMode ? "text-emerald-400" : "text-emerald-600";
}

function MetricCard({ icon: Icon, label, value, color, darkMode }) {
  return (
    <Card data-testid={`metric-card-${label.toLowerCase().replace(/\s/g,"-")}`}
      className={`${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} transition-shadow hover:shadow-md`}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{value ?? "—"}</p>
          <p className={`text-xs mt-0.5 ${darkMode ? "text-gray-600" : "text-gray-500"}`}>{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AlertRow({ children, level, darkMode }) {
  const bg = level === "critical"
    ? (darkMode ? "bg-red-900/30 border-red-700/50" : "bg-red-50 border-red-200")
    : (darkMode ? "bg-yellow-900/30 border-yellow-700/50" : "bg-yellow-50 border-yellow-200");
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${bg} text-sm`}>
      {level === "critical"
        ? <XCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? "text-red-400" : "text-red-500"}`} />
        : <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? "text-yellow-400" : "text-yellow-500"}`} />}
      <span className={darkMode ? "text-gray-200" : "text-gray-700"}>{children}</span>
    </div>
  );
}

export default function SystemHealth() {
  const { darkMode } = useSettings();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  const [rlData, setRlData] = useState(null);
  const [rlLoading, setRlLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/api/superadmin/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealth(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error("Health fetch error:", err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        setError("no_permission");
      } else {
        setError("fetch_error");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRateLimits = useCallback(async () => {
    setRlLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/api/rate-limit-events?hours=24&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRlData(data);
    } catch (err) {
      // Silent: rate limits panel is optional and shouldn't break health page
      console.error("Rate limit events fetch error:", err);
    } finally {
      setRlLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    fetchRateLimits();
    const interval = setInterval(() => {
      fetchHealth();
      fetchRateLimits();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchHealth, fetchRateLimits]);

  const bg = darkMode ? "bg-gray-900" : "bg-gray-50";
  const card = darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const text = darkMode ? "text-white" : "text-gray-900";
  const muted = darkMode ? "text-gray-600" : "text-gray-500";

  const allClear = health?.alerts?.ok;
  const criticalCount = health?.alerts?.critical ?? 0;
  const warningCount  = health?.alerts?.warning  ?? 0;

  return (
    <div data-testid="system-health-page" className={`min-h-screen ${bg} p-6 space-y-6`}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${text}`}>Salud del Sistema</h1>
          <p className={`text-sm mt-1 ${muted}`}>
            Monitorización en tiempo real · auto-refresco 60s
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className={`text-xs ${muted}`}>
              Actualizado: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button
            data-testid="refresh-health-btn"
            variant="outline"
            size="sm"
            onClick={() => { setLoading(true); fetchHealth(); }}
            disabled={loading}
            className={darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : ""}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Refrescar</span>
          </Button>
        </div>
      </div>

      {/* Global status banner */}
      {!loading && health && (
        <div data-testid="global-status-banner"
          className={`flex items-center gap-3 p-4 rounded-xl border ${
            criticalCount > 0
              ? (darkMode ? "bg-red-900/40 border-red-700" : "bg-red-50 border-red-300")
              : warningCount > 0
              ? (darkMode ? "bg-yellow-900/40 border-yellow-700" : "bg-yellow-50 border-yellow-300")
              : (darkMode ? "bg-emerald-900/40 border-emerald-700" : "bg-emerald-50 border-emerald-300")
          }`}>
          {criticalCount > 0
            ? <XCircle className={`w-6 h-6 ${darkMode ? "text-red-400" : "text-red-600"}`} />
            : warningCount > 0
            ? <AlertTriangle className={`w-6 h-6 ${darkMode ? "text-yellow-400" : "text-yellow-600"}`} />
            : <CheckCircle2 className={`w-6 h-6 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} />}
          <div>
            <p className={`font-semibold ${
              criticalCount > 0 ? severityColor("critical", darkMode)
              : warningCount > 0 ? severityColor("warning", darkMode)
              : severityColor("ok", darkMode)}`}>
              {criticalCount > 0
                ? `${criticalCount} alerta${criticalCount > 1 ? "s" : ""} crítica${criticalCount > 1 ? "s" : ""} — acción inmediata requerida`
                : warningCount > 0
                ? `${warningCount} aviso${warningCount > 1 ? "s" : ""} — revisión recomendada`
                : "Sistema en perfecto estado"}
            </p>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className={`w-8 h-8 animate-spin ${muted}`} />
        </div>
      )}

      {!loading && error === "no_permission" && (
        <div data-testid="health-no-permission"
          className={`flex flex-col items-center justify-center py-24 gap-4 ${muted}`}>
          <XCircle className="w-12 h-12 text-red-500" />
          <p className={`text-lg font-semibold ${text}`}>Acceso no autorizado</p>
          <p className="text-sm">Esta sección es exclusiva para Super Administradores.</p>
        </div>
      )}

      {!loading && error === "fetch_error" && (
        <div data-testid="health-fetch-error"
          className={`flex flex-col items-center justify-center py-24 gap-4 ${muted}`}>
          <AlertTriangle className="w-12 h-12 text-yellow-500" />
          <p className={`text-lg font-semibold ${text}`}>Error al cargar los datos</p>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchHealth(); }}>
            <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
          </Button>
        </div>
      )}

      {!loading && health && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Building2} label="Tiendas activas" value={health.stores_summary?.active} color="bg-blue-500" darkMode={darkMode} />
            <MetricCard icon={ShoppingBag} label="Alquileres activos" value={health.global_metrics?.active_rentals} color="bg-violet-500" darkMode={darkMode} />
            <MetricCard icon={Users} label="Clientes totales" value={health.global_metrics?.total_customers?.toLocaleString()} color="bg-teal-500" darkMode={darkMode} />
            <MetricCard icon={Package} label="Artículos en sistema" value={health.global_metrics?.total_items?.toLocaleString()} color="bg-orange-500" darkMode={darkMode} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Sesiones de caja sin cerrar */}
            <Card data-testid="unclosed-sessions-card" className={`${card} border`}>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-2 text-base ${text}`}>
                  <CalendarClock className="w-4 h-4 text-red-500" />
                  Sesiones de caja sin cerrar
                  <Badge variant={health.unclosed_sessions?.length > 0 ? "destructive" : "secondary"}
                    className="ml-auto text-xs">
                    {health.unclosed_sessions?.length ?? 0}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {health.unclosed_sessions?.length === 0 ? (
                  <div className={`flex items-center gap-2 text-sm ${muted}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Sin alertas
                  </div>
                ) : (
                  health.unclosed_sessions.map((s) => (
                    <AlertRow key={`us-${s.store_id}-${s.session_date}`} level={s.days_overdue >= 2 ? "critical" : "warning"} darkMode={darkMode}>
                      <span className="font-medium">{s.store_name}</span>
                      {" — sesión del "}<span className="font-medium">{s.session_date}</span>
                      {" lleva "}<span className={`font-bold ${s.days_overdue >= 2 ? (darkMode ? "text-red-300" : "text-red-700") : ""}`}>
                        {s.days_overdue} día{s.days_overdue !== 1 ? "s" : ""} sin cerrar
                      </span>
                    </AlertRow>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Artículos en estado dirty */}
            <Card data-testid="dirty-items-card" className={`${card} border`}>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-2 text-base ${text}`}>
                  <WrenchIcon className="w-4 h-4 text-yellow-500" />
                  Artículos pendientes de revisión (dirty)
                  <Badge variant={health.dirty_items_by_store?.length > 0 ? "secondary" : "secondary"}
                    className={`ml-auto text-xs ${health.global_metrics?.total_dirty_items > 0 ? "bg-yellow-100 text-yellow-800" : ""}`}>
                    {health.global_metrics?.total_dirty_items ?? 0} total
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {health.dirty_items_by_store?.length === 0 ? (
                  <div className={`flex items-center gap-2 text-sm ${muted}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Sin artículos pendientes
                  </div>
                ) : (
                  health.dirty_items_by_store.map((d) => (
                    <AlertRow key={`dirty-${d.store_id || d.store_name}`} level="warning" darkMode={darkMode}>
                      <span className="font-medium">{d.store_name}</span>
                      {" — "}<span className="font-bold">{d.count}</span>
                      {" artículo{d.count !== 1 ? 's' : ''} pendiente{d.count !== 1 ? 's' : ''} de inspección"}
                    </AlertRow>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Resumen de planes */}
            <Card data-testid="plans-summary-card" className={`${card} border`}>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-2 text-base ${text}`}>
                  <Building2 className="w-4 h-4 text-blue-500" />
                  Distribución de planes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(health.stores_summary?.by_plan ?? {}).map(([plan, count]) => (
                    <div key={plan} className={`flex items-center justify-between px-3 py-2 rounded-lg ${darkMode ? "bg-gray-700/60" : "bg-gray-50"}`}>
                      <span className={`text-sm capitalize ${muted}`}>{plan}</span>
                      <span className={`text-sm font-bold ${text}`}>{count}</span>
                    </div>
                  ))}
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${darkMode ? "bg-gray-700/60" : "bg-gray-50"} col-span-2`}>
                    <span className={`text-sm font-medium ${text}`}>Total tiendas</span>
                    <span className={`text-sm font-bold ${text}`}>{health.stores_summary?.total}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Estado global */}
            <Card data-testid="global-metrics-card" className={`${card} border`}>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-2 text-base ${text}`}>
                  <Clock className="w-4 h-4 text-violet-500" />
                  Métricas globales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { label: "Alquileres activos / parciales", value: health.global_metrics?.active_rentals },
                    { label: "Clientes registrados", value: health.global_metrics?.total_customers?.toLocaleString() },
                    { label: "Artículos en inventario", value: health.global_metrics?.total_items?.toLocaleString() },
                    { label: "Artículos en estado dirty", value: health.global_metrics?.total_dirty_items },
                    { label: "Sesiones de caja abiertas (atrasadas)", value: health.global_metrics?.unclosed_sessions_count },
                  ].map(({ label, value }) => (
                    <div key={label} className={`flex items-center justify-between text-sm py-1 border-b last:border-0 ${darkMode ? "border-gray-700" : "border-gray-100"}`}>
                      <span className={muted}>{label}</span>
                      <span className={`font-semibold ${text}`}>{value ?? 0}</span>
                    </div>
                  ))}
                  <p className={`text-xs pt-1 ${muted}`}>
                    Última actualización: {new Date(health.last_updated).toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* ── Rate-limit events (last 24h) ─────────────────────────── */}
          <Card data-testid="rate-limits-panel" className={card}>
            <CardHeader className="pb-3">
              <CardTitle className={`flex items-center justify-between text-base ${text}`}>
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-indigo-500" />
                  Peticiones bloqueadas por rate limit (últimas 24h)
                </span>
                <span className={`text-xs font-normal ${muted}`}>
                  {rlLoading ? "Cargando…" : `${rlData?.total ?? 0} eventos`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!rlData || rlData.total === 0) && !rlLoading && (
                <p className={`text-sm ${muted}`}>Sin eventos 429 en las últimas 24h.</p>
              )}
              {rlData && rlData.total > 0 && (
                <div className="space-y-4">
                  {rlData.top_endpoints?.length > 0 && (
                    <div>
                      <p className={`text-xs uppercase tracking-wide mb-2 ${muted}`}>Top endpoints</p>
                      <div className="flex flex-wrap gap-2" data-testid="rl-top-endpoints">
                        {rlData.top_endpoints.map((t) => (
                          <Badge key={t.endpoint} variant="secondary" className="font-mono text-xs">
                            {t.endpoint} · <span className="ml-1 font-bold">{t.count}</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className={`text-xs uppercase tracking-wide mb-2 ${muted}`}>Eventos recientes</p>
                    <div className={`max-h-72 overflow-y-auto rounded border ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                      <table className="w-full text-xs">
                        <thead className={darkMode ? "bg-gray-900" : "bg-gray-50"}>
                          <tr>
                            <th className={`text-left px-3 py-2 ${muted}`}>Hora</th>
                            <th className={`text-left px-3 py-2 ${muted}`}>Endpoint</th>
                            <th className={`text-left px-3 py-2 ${muted}`}>IP</th>
                            <th className={`text-left px-3 py-2 ${muted}`}>Usuario</th>
                          </tr>
                        </thead>
                        <tbody data-testid="rl-events-table">
                          {rlData.events.slice(0, 50).map((e) => (
                            <tr key={e.id} className={`border-t ${darkMode ? "border-gray-700" : "border-gray-100"}`}>
                              <td className={`px-3 py-1.5 whitespace-nowrap ${muted}`}>
                                {new Date(e.created_at).toLocaleTimeString()}
                              </td>
                              <td className={`px-3 py-1.5 font-mono ${text}`}>{e.endpoint}</td>
                              <td className={`px-3 py-1.5 font-mono ${muted}`}>{e.client_ip || "—"}</td>
                              <td className={`px-3 py-1.5 ${muted}`}>{e.username || "(anónimo)"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

        </>
      )}
    </div>
  );
}
